import { useEffect, useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import TextAlign from "@tiptap/extension-text-align";
import { TextStyle, Color } from "@tiptap/extension-text-style";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Bold, Italic, Underline, Heading1, Heading2, Heading3, Pilcrow, List, ListOrdered, Link2, Unlink,
  AlignLeft, AlignCenter, AlignRight, Undo2, Redo2, Braces, Palette,
} from "lucide-react";

const COLORS = ["#000000", "#475569", "#B91C1C", "#C2410C", "#15803D", "#1D4ED8", "#7E22CE"];

const escapeHtml = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
export const toEditorHtml = (v) => (!v ? "" : /<(p|br|div|h[1-6]|ul|ol|li|strong|em|u|span|a)\b/i.test(v)
  ? v : v.split("\n").map((l) => `<p>${escapeHtml(l)}</p>`).join(""));
export const htmlToText = (h) => new DOMParser().parseFromString(toEditorHtml(h), "text/html").body.textContent || "";

function Btn({ on, active, title, children, testid }) {
  return (
    <button type="button" title={title} onMouseDown={(e) => e.preventDefault()} onClick={on} data-testid={testid}
      className={`h-8 w-8 grid place-items-center rounded-md transition-colors ${active ? "bg-[#0B132B] text-white" : "text-slate-600 hover:bg-slate-200"}`}>
      {children}
    </button>
  );
}

function Toolbar({ editor, placeholders, testid, headings, colors }) {
  const [colorOpen, setColorOpen] = useState(false);
  const c = () => editor.chain().focus();
  const pickColor = (col) => { c().setColor(col).run(); setColorOpen(false); };
  const setLink = () => {
    const prev = editor.getAttributes("link").href || "https://";
    const url = window.prompt("Link-Adresse (https://… oder mailto:…)", prev);
    if (url === null) return;
    if (!url.trim()) { c().unsetLink().run(); return; }
    c().extendMarkRange("link").setLink({ href: url.trim() }).run();
  };
  const sep = <span className="w-px h-5 bg-slate-300 mx-1" />;
  return (
    <div className="flex flex-wrap items-center gap-0.5 border-b border-slate-200 bg-slate-50 px-1.5 py-1 rounded-t-lg" data-testid={`${testid}-toolbar`}>
      <Btn title="Fett" on={() => c().toggleBold().run()} active={editor.isActive("bold")} testid={`${testid}-bold`}><Bold size={15} /></Btn>
      <Btn title="Kursiv" on={() => c().toggleItalic().run()} active={editor.isActive("italic")} testid={`${testid}-italic`}><Italic size={15} /></Btn>
      <Btn title="Unterstrichen" on={() => c().toggleUnderline().run()} active={editor.isActive("underline")} testid={`${testid}-underline`}><Underline size={15} /></Btn>
      {sep}
      {headings.includes(1) && <Btn title="Überschrift 1" on={() => c().toggleHeading({ level: 1 }).run()} active={editor.isActive("heading", { level: 1 })} testid={`${testid}-h1`}><Heading1 size={15} /></Btn>}
      <Btn title="Überschrift" on={() => c().toggleHeading({ level: 2 }).run()} active={editor.isActive("heading", { level: 2 })} testid={`${testid}-h2`}><Heading2 size={15} /></Btn>
      <Btn title="Zwischenüberschrift" on={() => c().toggleHeading({ level: 3 }).run()} active={editor.isActive("heading", { level: 3 })} testid={`${testid}-h3`}><Heading3 size={15} /></Btn>
      <Btn title="Normaler Text" on={() => c().setParagraph().run()} active={editor.isActive("paragraph")} testid={`${testid}-p`}><Pilcrow size={15} /></Btn>
      {sep}
      <Btn title="Aufzählung" on={() => c().toggleBulletList().run()} active={editor.isActive("bulletList")} testid={`${testid}-ul`}><List size={15} /></Btn>
      <Btn title="Nummerierung" on={() => c().toggleOrderedList().run()} active={editor.isActive("orderedList")} testid={`${testid}-ol`}><ListOrdered size={15} /></Btn>
      {sep}
      <Btn title="Link einfügen" on={setLink} active={editor.isActive("link")} testid={`${testid}-link`}><Link2 size={15} /></Btn>
      <Btn title="Link entfernen" on={() => c().unsetLink().run()} testid={`${testid}-unlink`}><Unlink size={15} /></Btn>
      {sep}
      <DropdownMenu open={colorOpen} onOpenChange={setColorOpen}>
        <DropdownMenuTrigger asChild>
          <button type="button" title="Textfarbe" className="h-8 px-1.5 flex items-center gap-1 rounded-md text-slate-600 hover:bg-slate-200" data-testid={`${testid}-color`}>
            <Palette size={15} /><span className="h-3 w-3 rounded-sm border border-slate-300" style={{ background: editor.getAttributes("textStyle").color || "#000000" }} />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="p-2">
          <div className="flex gap-1.5">
            {colors.map((col) => (
              <button key={col} type="button" onClick={() => pickColor(col)} className="h-6 w-6 rounded border border-slate-300" style={{ background: col }} data-testid={`${testid}-color-${col.slice(1)}`} />
            ))}
          </div>
          <label className="mt-2 flex items-center gap-2 text-xs text-slate-500">Eigene Farbe
            <input type="color" onChange={(e) => c().setColor(e.target.value).run()} onBlur={() => setColorOpen(false)} className="h-6 w-8" />
          </label>
        </DropdownMenuContent>
      </DropdownMenu>
      {sep}
      <Btn title="Linksbündig" on={() => c().setTextAlign("left").run()} active={editor.isActive({ textAlign: "left" })} testid={`${testid}-align-left`}><AlignLeft size={15} /></Btn>
      <Btn title="Zentriert" on={() => c().setTextAlign("center").run()} active={editor.isActive({ textAlign: "center" })} testid={`${testid}-align-center`}><AlignCenter size={15} /></Btn>
      <Btn title="Rechtsbündig" on={() => c().setTextAlign("right").run()} active={editor.isActive({ textAlign: "right" })} testid={`${testid}-align-right`}><AlignRight size={15} /></Btn>
      {sep}
      <Btn title="Rückgängig" on={() => c().undo().run()} testid={`${testid}-undo`}><Undo2 size={15} /></Btn>
      <Btn title="Wiederholen" on={() => c().redo().run()} testid={`${testid}-redo`}><Redo2 size={15} /></Btn>
      {placeholders?.length > 0 && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button type="button" className="ml-auto h-8 px-2 flex items-center gap-1.5 rounded-md text-xs font-medium text-blue-700 hover:bg-blue-50" data-testid={`${testid}-placeholder`}>
              <Braces size={14} /> Platzhalter
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {placeholders.map((p) => (
              <DropdownMenuItem key={p} onSelect={() => c().insertContent(`{{${p}}}`).run()} data-testid={`${testid}-ph-${p}`}>{`{{${p}}}`}</DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
}

export function RichEditor({ value, onChange, placeholders, testid = "rich-editor", minHeight = 220, headings = [2, 3], colors }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: headings }, link: { openOnClick: false, autolink: true } }),
      TextStyle, Color, TextAlign.configure({ types: ["heading", "paragraph"] }),
    ],
    content: toEditorHtml(value),
    shouldRerenderOnTransaction: true,
    onUpdate: ({ editor: ed }) => onChange(ed.isEmpty ? "" : ed.getHTML()),
  });

  useEffect(() => {
    if (!editor) { return; }
    const next = toEditorHtml(value);
    if (next !== editor.getHTML() && !(next === "" && editor.isEmpty)) {
      editor.commands.setContent(next, { emitUpdate: false });
    }
  }, [value, editor]);

  if (!editor) return null;
  return (
    <div className="mt-1 border border-slate-200 rounded-lg bg-white focus-within:ring-2 focus-within:ring-blue-200" data-testid={testid}>
      <Toolbar editor={editor} placeholders={placeholders} testid={testid} headings={headings} colors={colors || COLORS} />
      <EditorContent editor={editor} className="rich-content px-3 py-2 overflow-y-auto" style={{ minHeight, maxHeight: 420 }} data-testid={`${testid}-content`} />
    </div>
  );
}
